import { useTranslation } from 'react-i18next'
import { usePage } from '@inertiajs/react'
import { Home, RefreshCw, AlertTriangle } from 'lucide-react'

interface ErrorFallbackProps {
  error: Error | null
  resetError: () => void
}

export default function ErrorFallback({ resetError }: ErrorFallbackProps) {
  const { t } = useTranslation()

  // Try to get user from Inertia, but handle case where it's not available
  let user = null
  try {
    const page = usePage<{ user: any }>()
    user = page.props.user
  } catch (e) {
    // usePage not available, user will be null
  }

  const handleReload = () => {
    resetError()
    window.location.reload()
  }

  // Render without AppLayout when Inertia context is not available
  // This ensures the error boundary works even if Inertia crashes
  return (
    <div className="min-h-screen bg-base-100">{user && (
        <nav className="navbar bg-base-200 px-4">
          <div className="flex-1">
            <a href="/" className="btn btn-ghost text-xl">
              {t('common.fileExplorer')}
            </a>
          </div>
        </nav>
      )}
      <div className="flex items-center justify-center min-h-[calc(100vh-200px)] px-4">
        <div className="text-center max-w-2xl">
          {/* Error Icon */}
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-error/10 rounded-full">
              <AlertTriangle className="w-16 h-16 text-error" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-3xl font-semibold text-base-content mb-4">
            {t('errors.boundary.title')}
          </h2>

          {/* Message - User-friendly only, no technical details */}
          <p className="text-lg text-base-content/70 mb-8">
            {t('errors.boundary.message')}
          </p>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 justify-center">
            <button
              onClick={handleReload}
              className="btn btn-primary gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              {t('errors.boundary.reload')}
            </button>

            <a
              href="/"
              className="btn btn-ghost gap-2"
            >
              <Home className="w-5 h-5" />
              {t('errors.boundary.goHome')}
            </a>
          </div>

          {/* Help Text */}
          <p className="text-sm text-base-content/50 mt-8">
            {t('errors.boundary.helpText')}
          </p>
        </div>
      </div>
    </div>
  )
}
